/* eslint-disable react/require-default-props */
// BronzorLuxeATS.tsx
import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  Link,
  StyleSheet,
} from '@react-pdf/renderer';

/**
 * BronzorLuxeATS
 * - Fancy but ATS-safe (single column by default)
 * - Bullets: refined hanging indent with accent markers
 * - No icons/tables/images; all selectable text
 * - Optional two/three-column visuals when atsStrict = false (keeps DOM order L→R)
 */

const ACCENT = '#2563eb'; // elegant blue
const ACCENT_FADE = '#93c5fd';
const TEXT = '#111';
const MUTED = '#3f3f46';

const styles = StyleSheet.create({
  page: {
    paddingTop: 36,
    paddingBottom: 42,
    paddingHorizontal: 44,
    color: TEXT,
  },

  // Header
  name: {
    fontSize: 22,
    fontWeight: 'bold',
    textAlign: 'center',
    letterSpacing: 0.2,
  },
  tagline: { fontSize: 11, textAlign: 'center', marginTop: 3, color: MUTED },

  // Accent rules
  ruleWrap: { marginTop: 10, marginBottom: 10 },
  ruleTop: { height: 2.2, backgroundColor: ACCENT, opacity: 0.95 },
  ruleBottom: {
    height: 1,
    backgroundColor: ACCENT_FADE,
    marginTop: 3,
    opacity: 0.8,
  },

  // Contacts (text chips, ATS-friendly)
  contacts: {
    marginTop: 8,
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  chip: {
    fontSize: 9,
    paddingVertical: 3,
    paddingHorizontal: 7,
    margin: 3,
    borderWidth: 1,
    borderColor: ACCENT,
    borderRadius: 4,
  },

  // Section header with smallcaps feel
  section: { marginTop: 14 },
  headerRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 6 },
  headerText: {
    fontSize: 11.5,
    fontWeight: 'bold',
    color: ACCENT,
    letterSpacing: 0.9,
    textTransform: 'uppercase',
  },
  headerLine: {
    flex: 1,
    height: 1.4,
    backgroundColor: ACCENT_FADE,
    marginLeft: 8,
  },

  // Entries
  entry: { marginTop: 8 },
  entryTop: { fontSize: 10.5, fontWeight: 'bold' },
  entryMeta: { fontSize: 9, color: MUTED, marginTop: 2 },
  entrySub: { fontSize: 9, marginTop: 2 },

  // Paragraphs / small text
  p: { fontSize: 9, lineHeight: 1.45 },

  // Fancy bullets (hanging indent + accent marker)
  bullets: { marginTop: 6 },
  bulletRow: { flexDirection: 'row', marginBottom: 4 },
  bulletMarker: {
    width: 12,
    fontSize: 10,
    lineHeight: 0.8,
    textAlign: 'center',
    color: ACCENT,
  },
  bulletText: {
    flex: 1,
    fontSize: 9,
    lineHeight: 1.25,
  },

  skillsWrap: { marginTop: 6 },

  // Optional inline callout for metrics/impact
  callout: {
    fontSize: 9.2,
    fontWeight: 'bold',
    color: TEXT,
  },

  // Optional columns (when atsStrict=false)
  twoColWrap: { flexDirection: 'row', gap: 16 },
  twoCol: { flex: 1 },
  threeColWrap: { flexDirection: 'row', gap: 16 },
  threeCol: { flex: 1 },
});

type LinkLike = { label?: string; text?: string; href?: string };
type Experience = {
  company: string;
  role: string;
  start: string;
  end: string; // "Present" allowed
  location?: string;
  url?: string;
  bullets?: Array<
    string | { text: string; impact?: string } // impact renders as bold inline callout
  >;
  tech?: string[];
};
type Project = {
  name: string;
  blurb?: string;
  url?: string;
  tech?: string[];
  bullets?: string[];
};
type Education = {
  school: string;
  degree?: string;
  start?: string;
  end?: string;
  location?: string;
  gpa?: string;
};
type Cert = { title: string; org?: string; year?: string };
type Language = { name: string; level?: string };

export type BronzorLuxeATSData = {
  name: string;
  tagline?: string;
  contacts?: LinkLike[];
  summary?: string;
  experience?: Experience[];
  projects?: Project[];
  skills?: string[];
  education?: Education;
  certifications?: Cert[];
  languages?: Language[];
  references?: string;
};

const SectionHeader = ({ title }: any): JSX.Element => (
  <View style={styles.headerRow}>
    <Text style={styles.headerText}>{title}</Text>
    <View style={styles.headerLine} />
  </View>
);

const BulletList: React.FC<{
  items?: Array<string | { text: string; impact?: string }>;
}> = ({ items }) => {
  if (!items?.length) return null;
  return (
    <View style={styles.bullets}>
      {items.map((item, i) => {
        const text = typeof item === 'string' ? item : item.text;
        const impact =
          typeof item === 'string' ? undefined : item.impact?.trim();
        return (
          <View key={i} style={styles.bulletRow}>
            {/* Accent marker ─ looks cleaner than plain • */}
            <Text style={styles.bulletMarker}>•</Text>
            <Text style={styles.bulletText}>
              {text}
              {impact ? <Text style={styles.callout}> — {impact}</Text> : null}
            </Text>
          </View>
        );
      })}
    </View>
  );
};

export const BronzorTemplate: React.FC<{
  data: BronzorLuxeATSData;
  atsStrict?: boolean;
}> = ({ data, atsStrict = true }) => {
  const renderChip = (c: LinkLike, i: number): React.ReactNode => {
    const label = c.label ?? c.text ?? c.href ?? '';
    const inner = <Text style={styles.chip}>{label}</Text>;
    return c.href ? (
      <Link key={i} src={c.href}>
        {inner}
      </Link>
    ) : (
      <View key={i}>{inner}</View>
    );
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <Text style={styles.name}>{data.name}</Text>
        {data.tagline ? (
          <Text style={styles.tagline}>{data.tagline}</Text>
        ) : null}
        {data.contacts?.length ? (
          <View style={styles.contacts}>{data.contacts.map(renderChip)}</View>
        ) : null}
        <View style={styles.ruleWrap}>
          <View style={styles.ruleTop} />
          <View style={styles.ruleBottom} />
        </View>

        {/* Summary */}
        {data.summary ? (
          <View style={styles.section}>
            <SectionHeader title="Summary" />
            <Text style={styles.p}>{data.summary}</Text>
          </View>
        ) : null}

        {/* Experience */}
        {data.experience?.length ? (
          <View style={styles.section}>
            <SectionHeader title="Experience" />
            {data.experience.map((ex, i) => (
              <View key={i} style={styles.entry}>
                <Text style={styles.entryTop}>
                  {ex.role} — {ex.company}
                </Text>
                <Text style={styles.entryMeta}>
                  {ex.start} to {ex.end}
                  {ex.location ? `  •  ${ex.location}` : ''}
                  {ex.url ? `  •  ${ex.url}` : ''}
                </Text>
                {ex.tech?.length ? (
                  <Text style={styles.entrySub}>
                    Tech: {ex.tech.join(', ')}
                  </Text>
                ) : null}
                <BulletList items={ex.bullets} />
              </View>
            ))}
          </View>
        ) : null}

        {/* Projects */}
        {data.projects?.length ? (
          <View style={styles.section}>
            <SectionHeader title="Projects" />
            {atsStrict ? (
              <View>
                {data.projects.map((p, i) => (
                  <View key={i} style={styles.entry}>
                    <Text style={styles.entryTop}>{p.name}</Text>
                    {p.url ? (
                      <Text style={styles.entryMeta}>{p.url}</Text>
                    ) : null}
                    {p.tech?.length ? (
                      <Text style={styles.entrySub}>
                        Tech: {p.tech.join(', ')}
                      </Text>
                    ) : null}
                    {p.blurb ? <Text style={styles.p}>{p.blurb}</Text> : null}
                  </View>
                ))}
              </View>
            ) : (
              <View style={styles.twoColWrap}>
                <View style={styles.twoCol}>
                  {data.projects
                    .filter((_, idx) => idx % 2 === 0)
                    .map((p, i) => (
                      <View key={i} style={styles.entry}>
                        <Text style={styles.entryTop}>{p.name}</Text>
                        {p.url ? (
                          <Text style={styles.entryMeta}>{p.url}</Text>
                        ) : null}
                        {p.tech?.length ? (
                          <Text style={styles.entrySub}>
                            Tech: {p.tech.join(', ')}
                          </Text>
                        ) : null}
                        {p.blurb ? (
                          <Text style={styles.p}>{p.blurb}</Text>
                        ) : null}
                      </View>
                    ))}
                </View>
                <View style={styles.twoCol}>
                  {data.projects
                    .filter((_, idx) => idx % 2 === 1)
                    .map((p, i) => (
                      <View key={i} style={styles.entry}>
                        <Text style={styles.entryTop}>{p.name}</Text>
                        {p.url ? (
                          <Text style={styles.entryMeta}>{p.url}</Text>
                        ) : null}
                        {p.tech?.length ? (
                          <Text style={styles.entrySub}>
                            Tech: {p.tech.join(', ')}
                          </Text>
                        ) : null}
                        {p.blurb ? (
                          <Text style={styles.p}>{p.blurb}</Text>
                        ) : null}
                      </View>
                    ))}
                </View>
              </View>
            )}
          </View>
        ) : null}

        {/* Skills */}
        {data.skills?.length ? (
          <View style={styles.section}>
            <SectionHeader title="Skills" />
            <View style={styles.skillsWrap}>
              <Text style={styles.p}>{data.skills.join(', ')}</Text>
            </View>
          </View>
        ) : null}

        {/* Education */}
        {data.education ? (
          <View style={styles.section}>
            <SectionHeader title="Education" />
            <View style={styles.entry}>
              <Text style={styles.entryTop}>{data.education.school}</Text>
              <Text style={styles.entryMeta}>
                {[data.education.start, data.education.end]
                  .filter(Boolean)
                  .join(' to ')}
                {data.education.location
                  ? `  •  ${data.education.location}`
                  : ''}
              </Text>
              {data.education.degree ? (
                <Text style={styles.entrySub}>{data.education.degree}</Text>
              ) : null}
              {data.education.gpa ? (
                <Text style={styles.p}>GPA: {data.education.gpa}</Text>
              ) : null}
            </View>
          </View>
        ) : null}

        {/* Certifications */}
        {data.certifications?.length ? (
          <View style={styles.section}>
            <SectionHeader title="Certifications" />
            {data.certifications.map((c, i) => (
              <Text key={i} style={styles.p}>
                {c.title}
                {c.org ? ` — ${c.org}` : ''}
                {c.year ? ` (${c.year})` : ''}
              </Text>
            ))}
          </View>
        ) : null}

        {/* Languages */}
        {data.languages?.length ? (
          <View style={styles.section}>
            <SectionHeader title="Languages" />
            {data.languages.map((l, i) => (
              <Text key={i} style={styles.p}>
                {l.name}
                {l.level ? ` — ${l.level}` : ''}
              </Text>
            ))}
          </View>
        ) : null}

        {/* References */}
        {data.references ? (
          <View style={styles.section}>
            <SectionHeader title="References" />
            <Text style={styles.p}>{data.references}</Text>
          </View>
        ) : null}
      </Page>
    </Document>
  );
};
